import * as React from "react";
import { PortalLayout } from "../Api/GeneratedDTOs/ViewModels/Shared/PortalLayout"
import { IPortalAppContext } from "./appContext";
import { CrossSiteRequestForgeryTokenInfo } from "spry-react/utils/fetchExtensions";
import { AppRouter } from "../appRouter";
import { ErrorBoundary } from "../Utils/ErrorHandling/errorBoundary";
import { ErrorLogger } from "../Utils/ErrorHandling/errorLogger";
import { ErrorLoggerListener } from "../Utils/ErrorHandling/errorLoggerListener";
import { AppWithContext } from "./appWithContext";

interface WithLayout {
  layout: PortalLayout;
}

export interface Props {
  data: WithLayout;
  renderChildren: (
    appContext: IPortalAppContext,
  ) => React.ReactElement<any> | React.ReactElement<any>[] | React.ReactPortal | string | number | null | false;
  /**
   * Flag indicating if this page is a client side router page.  If it is, then renderChildren will not be called because
   * we know that the router will render the page.  If it is NOT a client side page, then no router will be rendered,
   * renderChildren will be called, and the context will indicate that there is noRouter so any client side links must
   * be rendered as non-client side links.
   */
  isClientSidePage?: boolean;
}
export interface State {
  csrfInfo: CrossSiteRequestForgeryTokenInfo;
}

/**
 * This is the top level component for the portal application.  It supports both client side routing and non-client side routing pages.
 * For non-client side routing pages, renderChildren is called to render the page.  For client side routing pages, the AppRouter is used to render the page.
 */
export class PortalApp extends React.Component<Props, State> {
  private readonly errorLogger: ErrorLogger;

  constructor(props: Props) {
    super(props);
    this.state = {
      csrfInfo: props.data.layout.interopInfo.csrfInfo,
    };
    this.errorLogger = new ErrorLogger(this.props.data.layout.portalSiteLinks.errorLoggingUrl, () => this.state.csrfInfo);
  }

  componentDidMount() {
    ErrorLoggerListener.start(this.errorLogger);
  }

  componentWillUnmount() {
    ErrorLoggerListener.stop();
  }

  render() {
    if (this.props.isClientSidePage) {
      return <AppRouter layout={this.props.data.layout} errorLogger={this.errorLogger} getCsrfInfo={() => this.state.csrfInfo} />;
    } else {
      return (
        <ErrorBoundary errorLogger={this.errorLogger}>
          <AppWithContext
            layout={this.props.data.layout}
            getCsrfInfo={() => this.state.csrfInfo}
            renderChildren={this.props.renderChildren}
            isClientSidePage={false}
          />
        </ErrorBoundary>
      );
    }
  }
}